/**
 * GoIcon API Service
 * Handles API calls to api.goicon.com endpoints via Netlify proxy
 * API key is provided from JWT payload
 */

import { normalizeGender, NormalizedGender } from '../utils/genderEventFilter';

export interface GoIconConfig {
  apiKey?: string;
  useProxy: boolean;
}

export class GoIconApiService {
  private config: GoIconConfig;

  constructor(config: GoIconConfig) {
    this.config = config;
  }

  /**
   * Get the proxy URL for GoIcon API requests
   */
  private getProxyUrl(): string {
    // Explicit override (same convention as supabaseAuth's exchangeEndpoint):
    // set REACT_APP_FUNCTIONS_BASE= (empty) under `netlify dev` so requests hit
    // the LOCAL functions instead of the deployed ones.
    const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
    if (explicitBase !== undefined) {
      return `${explicitBase.replace(/\/$/, '')}/.netlify/functions/goicon-api-proxy`;
    }
    // Plain `npm start` has no local functions - use the deployed proxy to avoid CORS
    if (process.env.NODE_ENV === 'development') {
      return 'https://wellness.goicon.co/.netlify/functions/goicon-api-proxy';
    }

    // In production, use relative path
    return '/.netlify/functions/goicon-api-proxy';
  }

  /**
   * Make a request to the GoIcon API via proxy
   */
  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}`;
    
    const apiKey = this.config.apiKey;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-GoIcon-API-Key': apiKey } : {}),
      ...options.headers,
    };

    console.log(`🌐 GoIcon API request (via proxy): ${options.method || 'GET'} ${endpoint}`);
    console.log(`🔑 API Key present: ${!!apiKey}`);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ GoIcon API error (${response.status}):`, errorText);
        throw new Error(`API error: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ GoIcon API response data:`, data);
      return data;
    } catch (error) {
      console.error(`❌ GoIcon API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Get list of residents for a facility (for STAFF users)
   * GET /facilities/{facilityId}/social/wellness-dashboard/residents
   */
  async getFacilityResidents(facilityId: string): Promise<any> {
    try {
      console.log(`🌐 getFacilityResidents called - facilityId: ${facilityId}, apiKey: ${!!this.config.apiKey}`);
      const response = await this.makeRequest(`/facilities/${facilityId}/social/wellness-dashboard/residents`);
      console.log(`✅ GoIcon API response for facility ${facilityId}:`, response);
      console.log(`✅ Response type:`, typeof response, 'Keys:', Object.keys(response || {}));
      console.log(`✅ Residents array:`, response?.residents);
      console.log(`✅ Retrieved ${response?.residents?.length || 0} residents for facility ${facilityId}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch facility residents from GoIcon API:', error);
      throw error;
    }
  }

  /**
   * Get the facility groups a resident belongs to.
   * GET /facilities/{facilityId}/groups/groupsForGroupMember?memberID={residentId}&withoutMembers=true
   * Returns the facilityGroups array from the response.
   */
  async getResidentGroups(facilityId: string, residentId: string): Promise<any[]> {
    const endpoint = `/facilities/${facilityId}/groups/groupsForGroupMember?memberID=${encodeURIComponent(residentId)}&withoutMembers=true`;
    const data = await this.makeRequest(endpoint);
    // API returns { facilityMemberGroups: [...] }
    const groups: any[] = data?.facilityMemberGroups ?? data?.facilityGroups ?? [];
    console.log(`👥 Resident ${residentId} belongs to ${groups.length} groups`);
    return groups;
  }

  /**
   * Resident gender from the GoIcon social profile.
   * GET /facilities/{facilityId}/social/users/{goiconResidentId}
   * Gender is an enhancement, not a dependency: any failure (network error, missing
   * field, unrecognized value) resolves to null so callers never filter on a guess.
   */
  async getResidentGender(facilityId: string, goiconResidentId: string): Promise<NormalizedGender> {
    try {
      const endpoint = `/facilities/${facilityId}/social/users/${goiconResidentId}`;
      const resp = await this.makeRequest(endpoint);
      const raw = resp?.gender ?? resp?.user?.gender ?? resp?.profile?.gender ?? resp?.v_gender;
      return normalizeGender(raw);
    } catch (error) {
      console.warn(`⚠️ Could not resolve gender for resident ${goiconResidentId}:`, error);
      return null;
    }
  }
}

/**
 * Factory function to create GoIcon API service
 * @param apiKey - GoIcon API key from JWT payload
 */
export function createGoIconApiService(apiKey?: string): GoIconApiService {
  return new GoIconApiService({
    apiKey,
    useProxy: true, // Always use proxy to avoid CORS
  });
}

